import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CITIES } from "@/lib/catalog";

const CITY_OPTIONS = CITIES.map((c) => c.name);

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";

const TIMES = [
  "Early morning", "Mid morning", "Lunchtime", "Afternoon",
  "After work", "Evening", "Flexible",
];
const GOALS = [
  "Longevity", "Recovery & injury management", "Strength", "Endurance",
  "Sleep", "Stress & focus", "Weight", "Keeping a routine",
];
const SERVICES = [
  "Gym access", "Cold therapy & contrast", "Massage",
  "Personal trainer", "Chiropractic",
];

const FREQUENCY = ["Weekly", "Every two weeks", "Monthly", "Every few months", "A few times a year"];
const NOTICE    = ["Same week", "About a week", "Two to three weeks", "A month or more"];
const COMPANIONS = ["Alone", "With a partner", "With colleagues", "With family"];

const EMPTY = {
  phone: "", home_city: "",
  cities: [],
  frequency: "", nights: "", notice: "", companions: "",
  areas: "", accommodation: "",
  trip_services: {},
  times: [], goals: [],
};

export default function MemberProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [baseline, setBaseline] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [cityInput, setCityInput] = useState("");

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate("/"); return; }
    if (user.role === "admin") { navigate("/admin"); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/profile");
        const next = {
          phone: data.phone || "",
          home_city: data.home_city || "",
          cities: data.cities || [],
          frequency: data.frequency || "",
          nights: data.nights ?? "",
          notice: data.notice || "",
          companions: data.companions || "",
          areas: data.areas || "",
          accommodation: data.accommodation || "",
          trip_services: data.trip_services || {},
          times: data.times || [],
          goals: data.goals || [],
        };
        setForm(next); setBaseline(next);
      } catch (e) {
        setNotice(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);

  const addCity = () => {
    const v = cityInput.trim();
    if (!v || form.cities.includes(v)) { setCityInput(""); return; }
    setForm((f) => ({ ...f, cities: [...f.cities, v] }));
    setCityInput("");
  };
  const removeCity = (c) => setForm((f) => ({ ...f, cities: f.cities.filter((x) => x !== c) }));
  const moveCity = (c, dir) => setForm((f) => {
    const arr = [...f.cities];
    const i = arr.indexOf(c); if (i < 0) return f;
    const j = i + dir; if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, cities: arr };
  });

  const toggleChip = (key, v) => setForm((f) => {
    const arr = f[key]; const on = arr.includes(v);
    return { ...f, [key]: on ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const setSvcQty = (svc, delta) => setForm((f) => {
    const cur = f.trip_services[svc] || 0;
    const next = Math.max(0, Math.min(50, cur + delta));
    const map = { ...f.trip_services };
    if (next === 0) delete map[svc]; else map[svc] = next;
    return { ...f, trip_services: map };
  });

  const save = async () => {
    setSaving(true); setNotice("");
    try {
      await api.put("/auth/profile", {
        ...form,
        nights: form.nights === "" ? null : Number(form.nights),
      });
      setBaseline(form);
      setNotice("Saved");
      navigate("/member/profile");
    } catch (e) {
      setNotice(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };
  const revert = () => setForm(baseline);

  if (!user || user === false) return null;

  return (
    <div
      data-testid="member-profile-page"
      className="min-h-screen text-neutral-200"
      style={{
        background: "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 45%, #050506 85%, #000000 100%)",
        fontFamily: "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      <header className="sticky top-0 z-30 backdrop-blur-md" style={{ borderBottom: "1px solid #22252A", background: "rgba(0,0,0,0.72)" }}>
        <div className="mx-auto max-w-4xl px-5 py-4 flex items-center gap-4">
          <Link to="/member" className="flex items-center gap-3 min-w-0">
            <div>
              <div aria-hidden className="w-40 h-4"
                style={{
                  background: METAL_BG,
                  WebkitMaskImage: "url(/wellnet-logo.png)", maskImage: "url(/wellnet-logo.png)",
                  WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                  WebkitMaskSize: "contain", maskSize: "contain",
                  WebkitMaskPosition: "left center", maskPosition: "left center",
                }}
              />
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mt-1">Edit Profile</div>
            </div>
          </Link>
          <div className="ml-auto flex items-center gap-4 text-[10px] uppercase tracking-[0.25em]">
            <span className="text-neutral-400 hidden sm:inline">{user.email}</span>
            <button
              data-testid="profile-signout"
              onClick={async () => { await logout(); navigate("/"); }}
              className="border border-neutral-600 text-neutral-300 px-3 py-1.5 hover:bg-neutral-200 hover:text-black transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 pt-10 pb-40">
        <Link
          to="/member/profile"
          data-testid="profile-back"
          className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.3em] text-neutral-300 hover:text-neutral-100 hover:border-neutral-200 transition-colors"
          style={{ border: "1px solid #31353C", borderRadius: 8 }}
        >
          <span aria-hidden>←</span> Back
        </Link>
        <h1
          data-testid="profile-title"
          className="mt-5 font-semibold tracking-tight text-neutral-100"
          style={{
            fontFamily: "'Syne','Archivo',sans-serif",
            fontSize: "clamp(29px,4.6vw,44px)",
            lineHeight: 1.1,
          }}
        >
          Your profile
        </h1>
        <p className="mt-3 max-w-[60ch] text-neutral-400 text-[15.5px]">
          None of this is required. Anything you fill in is one less question when you ask us for a trip.
        </p>

        {loading ? (
          <div className="mt-12 text-xs uppercase tracking-[0.3em] text-neutral-500">Loading…</div>
        ) : (
          <div className="mt-12 flex flex-col gap-8">
            <Block title="You" hint="Your name and email come from your sign-up. Tell us if the rest changes.">
              <Grid>
                <Field label="Name"><input value={user.name || user.email.split("@")[0]} disabled className="profile-input" /></Field>
                <Field label="Email"><input value={user.email} disabled className="profile-input" /></Field>
                <Field label="Phone"><input data-testid="pf-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Best number for the concierge" className="profile-input" /></Field>
                <Field label="Home city"><input data-testid="pf-home" value={form.home_city} onChange={(e) => setForm({ ...form, home_city: e.target.value })} placeholder="Where you are based" className="profile-input" /></Field>
              </Grid>
            </Block>

            <Block title="Cities you travel to" hint="Most frequent at the top — that order tells us where to build cover first.">
              <div className="flex flex-col gap-2">
                {form.cities.length === 0 && <div className="text-sm text-neutral-500">No cities added yet.</div>}
                {form.cities.map((c, i) => (
                  <div key={c} className="flex items-center gap-2 rounded px-3 py-2" style={{ background: "#0B0C0F", border: "1px solid #22252A" }}>
                    <span className="flex-1 text-neutral-200 text-sm">{c}</span>
                    <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => moveCity(c, -1)} className="px-2 text-neutral-500 hover:text-neutral-100 disabled:opacity-30">↑</button>
                    <button type="button" aria-label="Move down" disabled={i === form.cities.length - 1} onClick={() => moveCity(c, 1)} className="px-2 text-neutral-500 hover:text-neutral-100 disabled:opacity-30">↓</button>
                    <button type="button" aria-label="Remove" onClick={() => removeCity(c)} data-testid={`city-remove-${i}`} className="px-2 text-neutral-500 hover:text-[#F1928A]">×</button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <select
                  data-testid="city-input"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  className="profile-input flex-1"
                  style={{
                    appearance: "none",
                    backgroundImage:
                      "linear-gradient(45deg, transparent 50%, #898F99 50%), linear-gradient(135deg, #898F99 50%, transparent 50%)",
                    backgroundPosition: "calc(100% - 18px) 51%, calc(100% - 13px) 51%",
                    backgroundSize: "5px 5px, 5px 5px",
                    backgroundRepeat: "no-repeat",
                    paddingRight: 38,
                  }}
                >
                  <option value="">Add a city…</option>
                  {CITY_OPTIONS.filter((c) => !form.cities.includes(c)).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <button type="button" data-testid="city-add" onClick={addCity} disabled={!cityInput} className="px-4 py-2 text-sm font-semibold disabled:opacity-40" style={{ border: "1px solid #31353C", color: "#C7CAD1" }}>Add</button>
              </div>
            </Block>

            <Block title="How you travel" hint="Rough is fine. It decides how far ahead we hold slots and how much we line up.">
              <Grid>
                <Field label="How often"><Select data-testid="pf-frequency" value={form.frequency} onChange={(v) => setForm({ ...form, frequency: v })} options={FREQUENCY} /></Field>
                <Field label="Typical trip, in nights"><input data-testid="pf-nights" type="number" min="1" max="365" value={form.nights} onChange={(e) => setForm({ ...form, nights: e.target.value })} placeholder="4" className="profile-input" /></Field>
                <Field label="Notice you usually have"><Select data-testid="pf-notice" value={form.notice} onChange={(v) => setForm({ ...form, notice: v })} options={NOTICE} /></Field>
                <Field label="Who you travel with"><Select data-testid="pf-companions" value={form.companions} onChange={(v) => setForm({ ...form, companions: v })} options={COMPANIONS} /></Field>
                <Field label="Areas you tend to stay in"><input data-testid="pf-areas" value={form.areas} onChange={(e) => setForm({ ...form, areas: e.target.value })} placeholder="Flatiron, Midtown West…" className="profile-input" /></Field>
                <Field label="Where you stay"><input data-testid="pf-accommodation" value={form.accommodation} onChange={(e) => setForm({ ...form, accommodation: e.target.value })} placeholder="Hotel, company apartment…" className="profile-input" /></Field>
              </Grid>
            </Block>

            <Block title="What a normal trip needs" hint="Sessions per trip, not per week. We use these as the starting point for every package and you can change them any time you ask.">
              <div className="flex flex-col gap-2">
                {SERVICES.map((svc) => {
                  const qty = form.trip_services[svc] || 0;
                  return (
                    <div key={svc} className="flex items-center gap-3 rounded px-3 py-2.5" style={{ background: "#0B0C0F", border: "1px solid #22252A" }}>
                      <span className="flex-1 text-sm text-neutral-200">{svc}</span>
                      <div className="flex items-center overflow-hidden" style={{ border: "1px solid #31353C", borderRadius: 8 }}>
                        <button type="button" data-testid={`svc-dec-${svc}`} onClick={() => setSvcQty(svc, -1)} className="w-8 h-[32px] text-neutral-400 hover:bg-neutral-800 hover:text-white">−</button>
                        <span data-testid={`svc-qty-${svc}`} className="w-10 h-[32px] leading-[32px] text-center font-semibold tabular-nums text-neutral-100" style={{ borderLeft: "1px solid #31353C", borderRight: "1px solid #31353C" }}>{qty}</span>
                        <button type="button" data-testid={`svc-inc-${svc}`} onClick={() => setSvcQty(svc, 1)} className="w-8 h-[32px] text-neutral-400 hover:bg-neutral-800 hover:text-white">+</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Block>

            <Block title="When it suits you" hint="Tap the ones that fit. We book around them where we can.">
              <Chips testid="times-chips" items={TIMES} selected={form.times} onToggle={(v) => toggleChip("times", v)} />
              <h3 className="text-[11px] font-semibold tracking-[0.14em] uppercase text-neutral-500 mt-6 mb-3">What you're after</h3>
              <p className="text-sm text-neutral-500 -mt-2 mb-3">What you want out of it, so we match the right people to you.</p>
              <Chips testid="goals-chips" items={GOALS} selected={form.goals} onToggle={(v) => toggleChip("goals", v)} />
            </Block>

            <div className="text-sm text-neutral-500 italic">
              Health questions aren't on this page. Anything about injuries, conditions or medication belongs in your intake form, where it is screened once and kept apart from everything here.
            </div>
          </div>
        )}
      </main>

      {/* Sticky save bar */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 flex items-center gap-3 px-5 py-3"
        style={{ background: "#000", borderTop: "1px solid #31353C", paddingBottom: "calc(12px + env(safe-area-inset-bottom,0px))" }}
      >
        <div className="mx-auto max-w-4xl w-full flex items-center gap-3">
          <div className="flex-1 min-w-0 text-[12px] text-neutral-500">
            {notice ? <span data-testid="profile-status" className={notice === "Saved" ? "text-[#7FC79A]" : "text-[#F1928A]"}>{notice}</span>
              : dirty ? "Unsaved changes" : "All changes saved"}
          </div>
          <button data-testid="profile-revert" type="button" onClick={revert} disabled={!dirty || saving} className="px-4 py-2 text-sm font-semibold disabled:opacity-40" style={{ border: "1px solid #31353C", color: "#C7CAD1" }}>Undo</button>
          <button data-testid="profile-save" type="button" onClick={save} disabled={!dirty || saving} className="px-5 py-2 text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed" style={{ background: METAL_BG, color: "#08090B" }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <style>{`
        .profile-input {
          width: 100%;
          background: #0B0C0F;
          border: 1px solid #22252A;
          color: #F3F4F6;
          border-radius: 8px;
          padding: 9px 11px;
          color-scheme: dark;
          font: inherit;
          transition: border-color .12s;
        }
        .profile-input:disabled { color: #8B8F97; background: #0a0b0d; }
        .profile-input:hover:not(:disabled) { border-color: #31353C; }
        .profile-input:focus:not(:disabled) { outline: none; border-color: #C7CAD1; }
        .profile-input::placeholder { color: #6F737B; }
      `}</style>
    </div>
  );
}

function Block({ title, hint, children }) {
  return (
    <section className="rounded-xl p-5 sm:p-6" style={{ background: "#101114", border: "1px solid #22252A" }}>
      <h2 className="text-[16.5px] font-semibold tracking-[-0.005em] text-neutral-100" style={{ fontFamily: "'Syne','Archivo',sans-serif" }}>{title}</h2>
      {hint && <p className="mt-1.5 text-sm text-neutral-500">{hint}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">{children}</div>;
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[11px] font-semibold tracking-[0.13em] uppercase text-neutral-500">{label}</span>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, ...rest }) {
  return (
    <select {...rest} value={value} onChange={(e) => onChange(e.target.value)} className="profile-input"
      style={{
        appearance: "none",
        backgroundImage:
          "linear-gradient(45deg, transparent 50%, #898F99 50%), linear-gradient(135deg, #898F99 50%, transparent 50%)",
        backgroundPosition: "calc(100% - 18px) 51%, calc(100% - 13px) 51%",
        backgroundSize: "5px 5px, 5px 5px",
        backgroundRepeat: "no-repeat",
        paddingRight: 38,
      }}
    >
      <option value="">Not sure yet</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Chips({ items, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.map((label) => {
        const on = selected.includes(label);
        return (
          <button
            key={label}
            type="button"
            data-testid={`chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onToggle(label)}
            className="whitespace-nowrap transition-colors"
            style={{
              border: "1px solid " + (on ? "#F3F4F6" : "#31353C"),
              background: on ? "#F3F4F6" : "#0B0C0F",
              color: on ? "#0A0B0D" : "#A6ABB4",
              borderRadius: 999, padding: "6px 13px", fontSize: 13.5,
              fontWeight: on ? 600 : 500, cursor: "pointer",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
